import React from 'react'

import type { BubbleGroupPosition } from '../../../MessageAttachment/types'
import type { LinkAttachmentBaseProps } from '../../types'
import BubbleTail from '../_shared/BubbleTail'
import CardBody from '../_shared/CardBody'
import CardShell from '../_shared/CardShell'
import CardThumbnail, {
  AUDIO_BG_CLASS,
  isPlayableAudio,
  isPlayableMedia,
} from '../_shared/CardThumbnail'
import { normalizeExternalHref } from '../_shared/normalizeExternalHref'
import { useChinPalette } from '../_shared/useChinPalette'

export interface ReceivedCardProps extends LinkAttachmentBaseProps {
  /**
   * Fired when the recipient activates the card. Behavior depends on how
   * the card is configured:
   *   - **Link app with a CTA** (FAQ / Form): the CTA owns navigation;
   *     `onClick` fires when the recipient taps the CTA itself, alongside
   *     `cta.onClick` (use for analytics).
   *   - **Link app with a URL** (Spotify / TikTok / generic link): the card
   *     chrome is an `<a target="_blank">` opening `url` — `onClick` fires
   *     alongside the navigation (use for analytics).
   *   - **Hero-image only card**: the card has no URL, so it renders as
   *     a button. `onClick` is the consumer's hook for opening a preview.
   *   - **Video / audio link previews**: the shell stays non-interactive
   *     so the native media controls remain operable — `onClick` is
   *     ignored in this configuration.
   */
  onClick?: () => void
  /**
   * Position of this card inside its same-author run. `'single'` / `'end'`
   * paint the accent-coloured tail, mirrored to the card's left edge;
   * `'first'` / `'middle'` omit it because a later bubble in the run owns it.
   * Defaults to `'single'`.
   */
  groupPosition?: BubbleGroupPosition
}

/**
 * The card the recipient sees in chat for a link attachment. Matches the
 * Received column of the messaging design system in Figma.
 */
const ReceivedCard: React.FC<ReceivedCardProps> = ({
  title,
  description,
  descriptionLines,
  url,
  mimeType,
  thumbnailUrl,
  sourceUrl,
  layout = 'featured',
  appIcon,
  cta,
  accentColor,
  status = 'ready',
  onClick,
  groupPosition = 'single',
}) => {
  // Video / audio link previews wrap the native media element — render a
  // plain non-interactive shell and let the media controls own clicks so
  // taps on play/pause/scrubber don't fire the outer card action.
  const isPlayingMedia = isPlayableMedia(mimeType, sourceUrl)
  // Normalize the URL so a bare hostname like `tr.ee/briemix` (used in
  // our own docs / stories) is treated as an external link instead of a
  // relative path. Returns `undefined` for empty / whitespace-only
  // values, so those fall through to the preview path instead of
  // producing an empty `href` on the shell anchor.
  const normalizedUrl = normalizeExternalHref(url)
  const shellHref =
    cta == null && normalizedUrl != null && !isPlayingMedia
      ? normalizedUrl
      : undefined
  const shellOnClick = cta == null && !isPlayingMedia ? onClick : undefined
  // A loading card is not yet an audio card: the scrape that would tell us has
  // not returned, so it keeps the ordinary 250px skeleton rather than the
  // intrinsic-height audio shell (whose `flex-1` hero would collapse to 0).
  const audioBg =
    status !== 'loading' && isPlayableAudio(mimeType, sourceUrl)
      ? AUDIO_BG_CLASS
      : undefined

  // When a CTA is set the shell isn't interactive — the CTA owns the
  // click target. Forward the card-level `onClick` to the CTA so
  // analytics / side-effect consumers still fire on activation while
  // preserving the CTA's own `onClick` callback.
  const wrappedCta =
    cta && onClick
      ? {
          ...cta,
          onClick: () => {
            onClick()
            cta.onClick?.()
          },
        }
      : cta

  const chin = useChinPalette({
    variant: 'light',
    accentColor,
    status,
    layout,
    thumbnailUrl,
    mimeType,
    sourceUrl,
  })

  return (
    <BubbleTail
      accentHex={chin.accentHex}
      side="receiver"
      // Only the last card in a same-author run carries the tail; the audio
      // player card is not a bubble, so it never does.
      visible={
        audioBg == null &&
        (groupPosition === 'single' || groupPosition === 'end')
      }
    >
      <CardShell
        variant="light"
        href={shellHref}
        onClick={shellOnClick}
        ariaLabel={title ?? 'Open attachment preview'}
        fixedHeight={layout === 'featured' && audioBg == null}
        accentHex={chin.accentHex}
        busy={chin.isLoading}
        bgClassName={audioBg}
        data-testid="link-attachment"
      >
        {layout === 'featured' && (
          <CardThumbnail
            variant="light"
            thumbnailUrl={chin.thumbnailUrl}
            sourceUrl={sourceUrl}
            title={title}
            mimeType={mimeType}
            isLoading={chin.isLoading}
            onImageError={chin.onImageError}
          />
        )}
        <CardBody
          chinTextColor={chin.chinTextColor}
          variant="light"
          ctaSurface={chin.surface}
          title={title}
          description={description}
          descriptionLines={descriptionLines}
          url={url}
          appIcon={appIcon}
          cta={wrappedCta}
          isLoading={chin.isLoading}
        />
      </CardShell>
    </BubbleTail>
  )
}

export default ReceivedCard
